/**
 * The hub's "3D Scans" API, as the app calls it (card `t_8ae1d20d`/E2, over
 * E1's frozen contract — `otto/src/server/routes/scans.routes.ts`).
 *
 * Every call here goes through the app's own `apiFetchRaw`, which is what makes
 * it work on all three transports the same way: a LAN hub over plain HTTP, a
 * cloud session over the WebRTC channel (its FormData path chunks large files
 * and reports progress), and Electron's `otto-local://` proxy. Nothing in this
 * module fetches by hand.
 *
 * The wire shapes are the hub's own, spelled out once (`HubScan`, `HubScanFile`)
 * because the app cannot import the hub's TypeScript — the hub's Prisma types
 * are generated in the other repo. A payload is always read through
 * `hubScansFrom`/`hubScanFrom`, which are total: a missing list, a null entry or
 * a partial row yields an empty list or a shrunken row, never a throw inside a
 * component (the failure mode that has cost this codebase the most, skill rule
 * 16).
 *
 * Auth is the app's own session — `apiFetchRaw` picks the credential — and the
 * upload is one multipart POST: repeated `files` parts, names preserved, plus
 * the optional text fields the hub reads to fill the row's columns.
 */

import { apiFetchRaw } from './api';
import { field, wireNumber, wireString } from '../utils/wire';

/** One file in a stored package, as the hub describes it. */
export interface HubScanFile {
  name: string;
  bytes: number;
  contentType: string;
  /** The hub's own path for the bytes (`/api/scans/<id>/file/<name>`). */
  apiPath: string;
}

/** A stored scan — the `scan` object every answer carries. */
export interface HubScan {
  id: string;
  name: string;
  label: string | null;
  sourceId: string | null;
  mode: string | null;
  roomCount: number | null;
  /** `torik.scan/v0` when the package carried a merged `scan.json`. */
  docSchema: string | null;
  capturedAt: string | null;
  fileCount: number;
  totalBytes: number;
  createdAt: string;
  updatedAt: string;
  files: HubScanFile[];
}

/** What the hub answers on upload and replace — the scan, plus its own notes. */
export interface ScanPackageAnswer {
  scan: HubScan;
  /** The hub's own sentences about a package it accepted anyway. */
  warnings: string[];
  /** Only on a replace: what the previous revision held. */
  replaced: { previousFiles: number; deletedObjects: number } | null;
}

/** The optional text fields of a package (the hub fills its columns from them). */
export interface ScanPackageFields {
  /** What the scan is called on the hub. */
  name?: string;
  /** The capture's own label (the phone's name for it). */
  label?: string;
  mode?: string;
  roomCount?: number;
  /** The device capture's id — the app's own key, carried for traceability. */
  sourceId?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function hubScanFileFrom(value: unknown): HubScanFile | null {
  if (!isRecord(value)) return null;
  const name = wireString(field(value, 'name'));
  if (!name) return null;
  return {
    name,
    bytes: wireNumber(field(value, 'bytes')) ?? 0,
    contentType: wireString(field(value, 'contentType')) ?? '',
    apiPath: wireString(field(value, 'apiPath')) ?? '',
  };
}

/** One row, total: a payload that names no id is dropped, not rendered half-way. */
export function hubScanFrom(value: unknown): HubScan | null {
  if (!isRecord(value)) return null;
  const id = wireString(field(value, 'id'));
  if (!id) return null;
  const files = field(value, 'files');
  return {
    id,
    name: wireString(field(value, 'name')) ?? '',
    label: wireString(field(value, 'label')) ?? null,
    sourceId: wireString(field(value, 'sourceId')) ?? null,
    mode: wireString(field(value, 'mode')) ?? null,
    roomCount: wireNumber(field(value, 'roomCount')) ?? null,
    docSchema: wireString(field(value, 'docSchema')) ?? null,
    capturedAt: wireString(field(value, 'capturedAt')) ?? null,
    fileCount: wireNumber(field(value, 'fileCount')) ?? 0,
    totalBytes: wireNumber(field(value, 'totalBytes')) ?? 0,
    createdAt: wireString(field(value, 'createdAt')) ?? '',
    updatedAt: wireString(field(value, 'updatedAt')) ?? '',
    files: Array.isArray(files)
      ? files.map(hubScanFileFrom).filter((file): file is HubScanFile => file !== null)
      : [],
  };
}

/** `{ scans: [...] }`, read defensively — a partial payload lists nothing rather than throwing. */
export function hubScansFrom(payload: unknown): HubScan[] {
  const list = isRecord(payload) ? field(payload, 'scans') : null;
  if (!Array.isArray(list)) return [];
  return list.map(hubScanFrom).filter((scan): scan is HubScan => scan !== null);
}

function warningsFrom(payload: unknown): string[] {
  const warnings = isRecord(payload) ? field(payload, 'warnings') : null;
  if (!Array.isArray(warnings)) return [];
  return warnings.map((entry) => wireString(entry)).filter((entry): entry is string => Boolean(entry));
}

/** The endpoint of one file's bytes; `?download=1` asks for an attachment. */
export function scanFileEndpoint(scanId: string, fileName: string, options: { download?: boolean } = {}): string {
  const path = `/scans/${encodeURIComponent(scanId)}/file/${encodeURIComponent(fileName)}`;
  return options.download ? `${path}?download=1` : path;
}

/**
 * The multipart body of a package: one `files` part per file, names preserved
 * (the hub stores the name it is given), then the optional text fields.
 */
export function scanPackageForm(files: File[], fields: ScanPackageFields = {}): FormData {
  const form = new FormData();
  for (const file of files) form.append('files', file, file.name);
  if (fields.name) form.append('name', fields.name);
  if (fields.label) form.append('label', fields.label);
  if (fields.mode) form.append('mode', fields.mode);
  if (typeof fields.roomCount === 'number' && Number.isFinite(fields.roomCount)) {
    form.append('roomCount', String(fields.roomCount));
  }
  if (fields.sourceId) form.append('sourceId', fields.sourceId);
  return form;
}

/** Every scan on the hub, newest first. */
export async function listHubScans(): Promise<HubScan[]> {
  const response = await apiFetchRaw('/scans');
  return hubScansFrom(await response.json());
}

/** One scan by id; `null` when the hub does not have it (404). */
export async function fetchHubScan(scanId: string): Promise<HubScan | null> {
  const response = await apiFetchRaw(`/scans/${encodeURIComponent(scanId)}`);
  return hubScanFrom(await response.json());
}

/**
 * Upload a package (`POST /api/scans`) — 201 with the stored scan, or the hub's
 * refusal as a thrown `ApiError` carrying `status` and the JSON body.
 *
 * `onUploadProgress` is real on the WebRTC transport (the API router chunks the
 * FormData and reports bytes); a plain-HTTP hub has no upload progress to
 * report, so a caller must treat "no tick yet" as unknown, not as zero.
 */
export async function uploadScanPackage(
  files: File[],
  fields: ScanPackageFields = {},
  onProgress?: (percent: number) => void,
): Promise<ScanPackageAnswer> {
  const response = await apiFetchRaw('/scans', {
    method: 'POST',
    body: scanPackageForm(files, fields),
    onUploadProgress: onProgress,
  });
  const payload: unknown = await response.json();
  const scan = hubScanFrom(payload);
  if (!scan) throw new Error('The hub answered a scan it cannot describe');
  return { scan, warnings: warningsFrom(payload), replaced: null };
}

/** Swap a stored scan's content (`PUT /api/scans/:id`). Same body as the upload. */
export async function replaceHubScan(
  scanId: string,
  files: File[],
  fields: ScanPackageFields = {},
  onProgress?: (percent: number) => void,
): Promise<ScanPackageAnswer> {
  const response = await apiFetchRaw(`/scans/${encodeURIComponent(scanId)}`, {
    method: 'PUT',
    body: scanPackageForm(files, fields),
    onUploadProgress: onProgress,
  });
  const payload: unknown = await response.json();
  const scan = hubScanFrom(payload);
  if (!scan) throw new Error('The hub answered a scan it cannot describe');
  const replaced = isRecord(payload) && isRecord(field(payload, 'replaced')) ? field(payload, 'replaced') : null;
  return {
    scan,
    warnings: warningsFrom(payload),
    replaced: replaced
      ? {
          previousFiles: wireNumber(field(replaced, 'previousFiles')) ?? 0,
          deletedObjects: wireNumber(field(replaced, 'deletedObjects')) ?? 0,
        }
      : null,
  };
}

/** Delete a scan and its bytes. */
export async function deleteHubScan(
  scanId: string,
): Promise<{ success: boolean; deletedFiles: number; deletedObjects: number }> {
  const response = await apiFetchRaw(`/scans/${encodeURIComponent(scanId)}`, { method: 'DELETE' });
  const payload: unknown = await response.json();
  return {
    success: isRecord(payload) ? field(payload, 'success') === true : false,
    deletedFiles: isRecord(payload) ? (wireNumber(field(payload, 'deletedFiles')) ?? 0) : 0,
    deletedObjects: isRecord(payload) ? (wireNumber(field(payload, 'deletedObjects')) ?? 0) : 0,
  };
}

/** One file's bytes, through the app's own transport (auth included). */
export async function fetchScanFile(
  scanId: string,
  fileName: string,
  options: { download?: boolean } = {},
): Promise<{ blob: Blob; contentType: string }> {
  const response = await apiFetchRaw(scanFileEndpoint(scanId, fileName, options));
  const blob = await response.blob();
  return { blob, contentType: response.headers.get('content-type') || 'application/octet-stream' };
}

/**
 * The file a preview should be drawn from, if the package carries one: the plan
 * the pipeline renders, then a still, then any image at all. `null` when the
 * package is geometry only (a raw phone capture — there is nothing to draw).
 */
export function scanPreviewFileName(scan: HubScan): string | null {
  const names = scan.files.map((file) => file.name);
  const preferred = ['plan.png', 'plan.svg', 'preview.png', 'thumbnail.png'];
  for (const candidate of preferred) {
    if (names.includes(candidate)) return candidate;
  }
  return names.find((name) => /\.(png|svg)$/i.test(name)) ?? null;
}

/**
 * The geometry a viewer opens, in the order the app prefers it.
 *
 * The rule itself moved to `utils/scanPackageGeometry.ts` (card t_92f7411f): the
 * dashboard's scan entry needs it in a module a Node test can import, and this
 * module reaches the app's transport. Re-exported here so every existing caller
 * keeps one import, and so there is still exactly ONE implementation.
 */
export { scanPrimaryModelName } from '../utils/scanPackageGeometry';
